import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { cn } from '../../../lib/cn';

/**
 * 伸びを効かせる時間（ms）。移動開始直後にこの時間だけ進行方向へ伸ばし、
 * 残り時間で果冻のように回弹しながら元の比率へ戻す。
 * スライド全体を 500ms に収めるため、styles.css の
 * `.cortex-sidebar-indicator-moving`（500ms）と `.cortex-sidebar-indicator-pill`（300ms）
 * と合わせて 200ms + 300ms = 500ms になるよう揃えている。
 */
const STRETCH_DURATION_MS = 200;
/** 伸び率の上限。1 + この値が進行方向（縦）の最大倍率になる。 */
const STRETCH_RATIO_MAX = 0.16;
/** 伸び率が上限に達する移動距離（px）。隣の項目への短い移動では控えめに伸びる。 */
const STRETCH_FULL_DISTANCE_PX = 320;
/** 縦に伸びた分を横で縮める比率（体積保存の見かけを作る）。 */
const STRETCH_WIDTH_RATIO = 0.5;

/** 選択項目の位置。左右は全項目が `mx-2` で揃うため CSS 側（left-2 right-2）に任せる。 */
interface ActiveItemBox {
  /** スクロールコンテナの内容座標での上端（px） */
  top: number;
  /** 項目の高さ（px）。nested 項目は親より低い */
  height: number;
}

/** 選択背景の状態。計測結果と、その結果をどう見せるか（表示・移動・伸び）をまとめて持つ。 */
interface IndicatorState {
  /** 直近に計測できた位置。選択項目が消えた後もフェードアウト用に保持する */
  box: ActiveItemBox | null;
  /** 選択項目が描画されているか（親の折り畳みで消えたら false） */
  visible: boolean;
  /** 移動としてアニメーションさせるか。初回配置だけ false にして飛び込みを防ぐ */
  animate: boolean;
  /** 伸び率（0 なら伸びなし） */
  stretch: number;
}

const INITIAL_STATE: IndicatorState = { box: null, visible: false, animate: false, stretch: 0 };

/**
 * OS の「視差効果を減らす」設定を読む。jsdom など matchMedia 非実装環境では false。
 */
function prefersReducedMotion(): boolean {
  if (typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * 選択中の項目の位置を、選択背景の親（ナビのスクロールコンテナ）座標で計測する。
 * 計測対象は `data-sidebar-item` ラッパーの直下要素 = アプリ側が renderNavLink で
 * 返したリンク要素で、`navLinkClassName` の余白がそのまま反映された矩形になる。
 */
function measureActiveItem(
  indicator: HTMLElement,
  activeKey: string | undefined,
): ActiveItemBox | null {
  const container = indicator.parentElement;
  if (container === null || activeKey === undefined || activeKey === '') return null;
  const wrapper = container.querySelector(`[data-sidebar-item="${CSS.escape(activeKey)}"]`);
  const link = wrapper?.firstElementChild ?? null;
  if (link === null) return null;
  const linkRect = link.getBoundingClientRect();
  // レイアウト未確定（jsdom や display:none 下）では高さが 0 になる。その場合は描画しない。
  if (linkRect.height === 0) return null;
  const containerRect = container.getBoundingClientRect();
  return {
    top: linkRect.top - containerRect.top + container.scrollTop,
    height: linkRect.height,
  };
}

export interface SidebarActiveIndicatorProps {
  /** 選択中のナビ項目 key。未選択・計測対象なしなら undefined */
  activeKey?: string;
}

/**
 * @graph-stack web-design-system
 * @graph-domain Engineering
 * @graph-business サイドバーの選択背景。ナビのスクロールコンテナ内で絶対配置され、選択中の項目の
 *  位置・高さを計測して現在地の下へスライドする。移動開始直後は進行方向へわずかに伸び、
 *  残り時間で果冻のように行き過ぎてから戻る（合計約 500ms）。移動距離が短いほど伸びは控えめ。
 *  初回配置とOSの視差効果低減設定ではアニメーションせず即座に配置する
 * @graph-connects none
 */
export function SidebarActiveIndicator({ activeKey }: SidebarActiveIndicatorProps): ReactNode {
  const ref = useRef<HTMLDivElement>(null);
  // 直近の計測結果。移動距離（= 伸び率）と初回判定に使うため state とは別に持つ。
  const lastBoxRef = useRef<ActiveItemBox | null>(null);
  const stretchTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const [state, setState] = useState<IndicatorState>(INITIAL_STATE);

  const sync = useCallback((): void => {
    const node = ref.current;
    if (node === null) return;
    const next = measureActiveItem(node, activeKey);
    const prev = lastBoxRef.current;
    lastBoxRef.current = next;
    if (next === null) {
      // 選択項目が消えた。位置は保ったままフェードアウトさせる（原点へ飛ばさない）。
      setState((current) =>
        current.visible ? { ...current, visible: false, stretch: 0 } : current,
      );
      return;
    }
    if (prev !== null && prev.top === next.top && prev.height === next.height) {
      setState((current) => (current.visible ? current : { ...current, visible: true }));
      return;
    }
    const distance = prev === null || prefersReducedMotion() ? 0 : Math.abs(next.top - prev.top);
    const stretch = STRETCH_RATIO_MAX * Math.min(1, distance / STRETCH_FULL_DISTANCE_PX);
    setState({ box: next, visible: true, animate: distance > 0, stretch });
    clearTimeout(stretchTimerRef.current);
    if (stretch > 0) {
      stretchTimerRef.current = setTimeout(() => {
        setState((current) => ({ ...current, stretch: 0 }));
      }, STRETCH_DURATION_MS);
    }
  }, [activeKey]);

  // 位置が動く操作（幅ドラッグ・折り畳み・項目の出入り・ページ遷移）は必ず再レンダーを伴うため、
  // 毎レンダー後に計測し直す。差分が無ければ同一 state を返すので再レンダーは起きない。
  useLayoutEffect(() => {
    sync();
  });

  // 再レンダーを伴わない window リサイズでも項目幅（= 折り返しのない高さ）が変わり得るので追従する。
  useEffect(() => {
    window.addEventListener('resize', sync);
    return () => {
      window.removeEventListener('resize', sync);
    };
  }, [sync]);

  // アンマウント時のみ伸びタイマーを破棄する（sync の再生成では消さない）。
  useEffect(
    () => () => {
      clearTimeout(stretchTimerRef.current);
    },
    [],
  );

  const { box, visible, animate, stretch } = state;
  return (
    <div
      ref={ref}
      aria-hidden="true"
      className={cn(
        'pointer-events-none absolute top-0 left-2 right-2 -z-10 will-change-transform cortex-sidebar-indicator',
        animate && 'cortex-sidebar-indicator-moving',
      )}
      style={{
        height: box?.height ?? 0,
        transform: `translate3d(0, ${String(box?.top ?? 0)}px, 0)`,
        opacity: visible ? 1 : 0,
      }}
    >
      <div
        className={cn(
          'h-full w-full rounded-lg bg-[var(--color-accent-muted)] cortex-sidebar-indicator-pill',
          stretch > 0 && 'cortex-sidebar-indicator-pill-stretch',
        )}
        style={{
          transform: `scale(${String(1 - stretch * STRETCH_WIDTH_RATIO)}, ${String(1 + stretch)})`,
        }}
      />
    </div>
  );
}
